/**
 * HTTP boundary for a project's dimensions (Plan J, J2).
 *
 * A *dimension* is the answer to "by which column of THIS project may a
 * scope, a filter or a grouping cut?". Three origins — a column the raw
 * table already has, a value translated through a tab of a Master the
 * client maintains, and a one-row SQL expression the reload
 * materialises.
 *
 * TWO INVARIANTS THIS MODULE EXISTS TO KEEP
 * -----------------------------------------
 * 1. **The shapes below mirror `builder/schemas/dimensions.py` and
 *    nothing else.** `status` in particular is a CLOSED union derived on
 *    every GET and never persisted (the `dimensions` table of portal
 *    migration 052 has no status column, deliberately — a stale
 *    persisted status is a green chip on a broken dimension). Adding a
 *    member on the backend is a frontend change too: the screen maps
 *    each one to a glyph and a word, and
 *    `__tests__/dimensions-pane.test.ts` fails when one has no entry.
 * 2. **Nothing here invents a value.** `column_type` is `null` when the
 *    live file does not carry the column — not `"unknown"`;
 *    `in_stored_schema` is `null` when the file could not be read at all
 *    — an unknown, not a "no". Both are the backend's own words in
 *    `DimensionOut`.
 *
 * It is its own module rather than an addition to `builder-api.ts` for
 * the reason `embed-api.ts` is: that file is past 900 lines and carries
 * an intentional null byte, so `grep` reports it as binary and needs
 * `-a`. Everything routes through `api.get/post/put/delete`, which is
 * the single point that injects the JWT and prefixes `/{tenant}/`
 * (`api.ts:99-101`) — a hand-rolled `fetch` here is how a caller earns a
 * 404 `no-prefix` under Plan E.
 */

import { api } from "@/lib/api";
import type { DisplayLabel, Project } from "@/types";

// ── The vocabularies, closed on both sides ───────────────────────────

/** `ck_dimensions_origin` (portal migration 052) — the whole set. */
export const DIMENSION_ORIGINS = ["column", "mapping", "computed"] as const;
export type DimensionOrigin = (typeof DIMENSION_ORIGINS)[number];

/**
 * What a dimension may be used FOR.
 *
 * `usable_as` defaults to `'[]'` — usable for NOTHING — and that empty
 * list is fail-closed, not "unset" (migration 052's own words). A row
 * seeded without it looks complete in any `SELECT` and makes every scope
 * naming it resolve `not_applicable`: the client sees nothing and the
 * build log does not say anything is wrong. That is why the form never
 * pre-checks a box and why the table renders an empty list as a visible
 * warning instead of a blank cell.
 */
export const DIMENSION_USABLE_AS = ["filter", "scope"] as const;
export type DimensionUsableAs = (typeof DIMENSION_USABLE_AS)[number];

/**
 * Derived per GET. Mirrors `schemas/dimensions.DimensionStatus`.
 *
 * Exported as a runtime array as well as a type because the status
 * dictionary has to be checked for completeness by a test, and a
 * TypeScript union alone cannot be iterated. This array is the only
 * mechanical thing between a status the backend adds later and a raw
 * identifier on the analyst's screen.
 */
export const DIMENSION_STATUSES = [
  "ok",
  "inert",
  "needs_reload",
  "schema_drift",
  "unresolvable",
] as const;
export type DimensionStatus = (typeof DIMENSION_STATUSES)[number];

// ── Payloads ─────────────────────────────────────────────────────────

/**
 * One row plus what a GET can DERIVE about it against the live file.
 *
 * `config` is the free JSONB the backend parses CLOSED-WORLD per origin.
 * It is typed as `Record<string, unknown>` here on purpose: narrowing it
 * to a per-origin union in the client would put a second definition of
 * the contract in a place that cannot enforce it, and the place that
 * can is `check_config_shape`.
 */
export interface DimensionOut {
  id: string;
  project_slug: string;
  key: string;
  label: DisplayLabel;
  origin: string;
  usable_as: string[];
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  status: string;
  /** The backend's own prose. Rendered verbatim — it is the sentence
   *  that says how to fix it. */
  status_detail: string;
  /** DESCRIBE type in the LIVE file, or `null` when it does not carry
   *  the column (yet, or any more). Never "unknown". */
  column_type: string | null;
  /** Whether `projects.data_source_schema` ALSO records the column.
   *  `null` = the live file could not be read at all. */
  in_stored_schema: boolean | null;
  /** Ids of the tenant's scopes whose `rules_json` names this key.
   *  Non-empty is what makes a delete or a key rename a 409. */
  used_by_scopes: string[];
}

export interface DimensionListOut {
  dimensions: DimensionOut[];
  total: number;
}

export interface DimensionUpdateOut {
  dimension: DimensionOut;
  rebuilds_scopes: string[];
  /** The backend's sentence about WHEN the edit takes effect. */
  effective: string;
}

export interface DimensionCreatePayload {
  key: string;
  label: Record<string, string>;
  origin: DimensionOrigin;
  usable_as: DimensionUsableAs[];
  config: Record<string, string>;
}

/** `origin` is deliberately absent: changing it would change what
 *  `source_column` MEANS while every scope rule kept pointing at the
 *  same key. Delete and re-create instead, which routes through the
 *  delete refusal on purpose. */
export interface DimensionUpdatePayload {
  key?: string;
  label?: Record<string, string>;
  usable_as?: DimensionUsableAs[];
  config?: Record<string, string>;
}

export interface DimensionValueOut {
  /** JSON-NATIVE: an INTEGER column answers with numbers, a BOOLEAN
   *  with booleans. The predicate binds type-directed, so a picker that
   *  stringified everything would author rules the build rejects. */
  value: unknown;
  row_count: number;
}

export interface DimensionValuesOut {
  values: DimensionValueOut[];
  /** The REAL distinct count after `search` — never capped, never the
   *  page length. */
  total: number;
  offset: number;
  limit: number;
  column: string;
  column_type: string;
  /** False means the values are real and useful for a filter, but a
   *  scope rule on them is refused at build time. */
  scopable: boolean;
  /** Rows whose value is NULL. They belong to NO restricted scope. */
  null_count: number;
  source: "project_file";
  version_key: string;
}

// ── Client ───────────────────────────────────────────────────────────

const base = (slug: string): string =>
  `/api/builder/projects/${encodeURIComponent(slug)}/dimensions`;

export const dimensionsApi = {
  list: (slug: string) => api.get<DimensionListOut>(base(slug)),
  create: (slug: string, payload: DimensionCreatePayload) =>
    api.post<DimensionOut>(base(slug), payload),
  update: (slug: string, id: string, payload: DimensionUpdatePayload) =>
    api.put<DimensionUpdateOut>(
      `${base(slug)}/${encodeURIComponent(id)}`,
      payload,
    ),
  remove: (slug: string, id: string) =>
    api.delete<void>(`${base(slug)}/${encodeURIComponent(id)}`),
  /** The distinct values a scope rule can be authored from. The page is
   *  bounded and `total` is not — the copy says which is which. */
  values: (
    slug: string,
    id: string,
    opts?: { search?: string; offset?: number; limit?: number },
  ) =>
    api.get<DimensionValuesOut>(`${base(slug)}/${encodeURIComponent(id)}/values`, {
      query: {
        search: opts?.search,
        offset: opts?.offset,
        limit: opts?.limit,
      },
    }),
};

// ── What columns may a dimension name ────────────────────────────────
//
// These two live next to the API rather than in the screen because they
// answer the same question the API validates: which raw column a
// dimension is allowed to point at. Keeping them here also keeps the
// import graph acyclic — the pane imports the modal, and both import
// this.

/**
 * The raw table a scope on this project would be cut from.
 *
 * Mirrors `service_dimensions._raw_table_for` → `_project_default_table`
 * → `_slug_to_identifier`: `config.table` when declared, else
 * `project_raw_{slug with dashes as underscores}`. Copied rather than
 * fetched because it is a pure function of data the hub already has, and
 * because all the implementations of it must agree on which table the
 * predicate filters.
 */
export function rawTableName(project: Project): string {
  const declared = project.data_source?.table;
  if (typeof declared === "string" && declared.trim() !== "") {
    return declared.trim();
  }
  return `project_raw_${project.slug.replace(/-/g, "_")}`;
}

/**
 * Column names from `projects.data_source_schema`, for the raw table.
 *
 * The snapshot has two shapes and the UI branches on `typeof entry`:
 * flat `{column: type}` for one materialised table, and a catalog
 * `{table: {column: type}}` for `duckdb_attach`. For the catalog only
 * the PROJECT'S raw table counts — a column of some other attached table
 * is not a column of the data a scope cuts, and offering it would author
 * a dimension the build refuses with `column_not_in_raw`.
 *
 * IMPORTANT, and the reason every caller pairs this with a free-text
 * escape: this is the schema the last reload RECORDED, while the backend
 * validates against the LIVE FILE. The two disagree in ordinary
 * situations that `service_dimensions`' docstring enumerates — the data
 * source cleared while the file survives, hand DDL, a reload that never
 * ran. So this list is a SUGGESTION, never a whitelist. A picker that
 * could only offer the record would make a real column unpickable.
 */
export function flattenRawColumns(
  schema: Record<string, string | Record<string, string>> | undefined,
  rawTable: string,
): string[] {
  if (schema === undefined) return [];
  const entries = Object.entries(schema);
  const nested = entries.filter(
    (pair): pair is [string, Record<string, string>] =>
      typeof pair[1] === "object" && pair[1] !== null,
  );
  if (nested.length === 0) {
    return entries
      .filter(([, v]) => typeof v === "string")
      .map(([k]) => k)
      .sort((a, b) => a.localeCompare(b));
  }
  const folded = rawTable.toLowerCase();
  const hit = nested.find(([table]) => table.toLowerCase() === folded);
  if (hit === undefined) return [];
  return Object.keys(hit[1]).sort((a, b) => a.localeCompare(b));
}
